import React, { useState } from "react";
import { ArrowLeft, CheckCircle2, Home, Loader2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { verifyUPI } from "@/services/customer";
import { getT } from "@/i18n/translations";


const UpiPage = () => {
  const t = getT();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedAmount = location.state?.selectedAmount || 0;

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);

  const handleSubmit = async () => {
    if (verified) {
      navigate("/upi-confirm", {
        state: {
          selectedAmount,
          mobile,
          upiId: verifiedData?.upi_handle,
          upiName: verifiedData?.name,
        },
      });
      return;
    }

    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobile || !mobileRegex.test(mobile)) {
      toast.error(t.upi_errInvalidMobile);
      return;
    }

    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("token_gen"));
      const res = await verifyUPI(token?.access_token, { mobileNumber: mobile, consent: true });
      const d = res?.data?.data;

      if (d?.is_valid === true) {
        toast.success(t.upi_successVerified);
        setVerifiedData(d);
        setVerified(true);
      } else {
        toast.error(d?.error_description || t.upi_errFailed);
      }
    } catch {
      toast.error(t.upi_errGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/30 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#ED1C16]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-[#ED1C16]" />
          </button>

          <p className="text-[15px] font-semibold text-black">
            {t.upi_pageTitle}
          </p>

          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <Home className="w-5 h-5 text-[#ED1C16]" />
          </button>
        </div>

        <div className="px-4 pb-8 space-y-3 flex-1">

          {/* MOBILE INPUT CARD */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-gray-100 mx-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2">
              {t.upi_mobileLabel}
            </p>

            <div className="flex items-center border border-gray-200 rounded-xl px-3.5 py-3 gap-2.5 focus-within:border-[#ED1C16] transition">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ED1C16"
                strokeWidth="2"
              >
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>

              <div className="w-px h-5 bg-gray-200" />

              <input
                type="tel"
                inputMode="numeric"
                placeholder={t.upi_mobilePlaceholder}
                value={mobile}
                maxLength={10}
                disabled={verified}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                }
                className="flex-1 bg-transparent outline-none text-[15px] text-black placeholder:text-gray-400 disabled:opacity-60"
              />

              {verified && (
                <CheckCircle2 size={18} className="text-[#ED1C16]" />
              )}
            </div>
          </div>

          {/* VERIFIED CARD */}
          {verified && verifiedData && (
            <div className="rounded-2xl px-5 py-4 shadow-sm border border-gray-100 mx-1 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={15} className="text-[#ED1C16]" />
                <p className="text-[11px] font-semibold text-[#ED1C16] uppercase">
                  {t.upi_verifiedLabel}
                </p>
              </div>

              <div className="space-y-2">
                {[
                  { label: t.upi_name, value: verifiedData?.name },
                  { label: t.upi_upiId, value: verifiedData?.upi_handle },
                  { label: t.upi_mobile, value: verifiedData?.mobile_number },
                ].map(
                  ({ label, value }) =>
                    value && (
                      <div
                        key={label}
                        className="flex justify-between items-center"
                      >
                        <p className="text-[12px] text-gray-500">{label}</p>
                        <p className="text-[13px] font-semibold text-black">
                          {value}
                        </p>
                      </div>
                    )
                )}
              </div>
            </div>
          )}

          {/* BUTTON */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-95 transition disabled:opacity-60 disabled:cursor-not-allowed mx-1"
            style={{ backgroundColor: "#ED1C16" }}
          >
            {loading
              ? t.upi_btnWait
              : verified
                ? t.upi_btnProceed
                : t.upi_btnVerify}
          </button>
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default UpiPage;